/**
 * Pin a tier: one row (Tier, Lane, Model, Reason, Pin). Lanes are limited to
 * what the workspace's policy permits; models come from the lane's price list.
 */

import { useState, type ReactNode } from "react";
import { Button } from "../../../ui/button.js";
import { Input } from "../../../ui/input.js";
import { NativeSelect } from "../../../ui/native-select.js";
import type { CatalogModel, Lane, Tier } from "./types.js";
import { EMPTY_CATALOG_LABEL, LANE_NAMES, priceLabel } from "./format.js";
import { JSON_HEADERS, runMutation } from "./http.js";

interface PinFormProps {
  workspaceId: string;
  tiers: Tier[];
  permittedLanes: Lane[];
  catalog: Record<Lane, CatalogModel[]>;
  /** True until the workspace's live routes have loaded. */
  disabled: boolean;
  onPinned: () => Promise<void>;
  onError: (message: string | null) => void;
  /** Extra controls at the end of the row (Close). */
  trailing?: ReactNode;
}

export function PinForm({
  workspaceId,
  tiers,
  permittedLanes,
  catalog,
  disabled,
  onPinned,
  onError,
  trailing,
}: PinFormProps) {
  const [pinTier, setPinTier] = useState<Tier>(tiers[0] ?? "efficient");
  const [pinLane, setPinLane] = useState<Lane | "">("");
  const [pinModel, setPinModel] = useState("");
  const [reason, setReason] = useState("");
  const [pinning, setPinning] = useState(false);

  const lane: Lane | undefined =
    pinLane !== "" && permittedLanes.includes(pinLane) ? pinLane : permittedLanes[0];
  const laneModels = lane ? (catalog[lane] ?? []) : [];
  const model = laneModels.some((m) => m.id === pinModel) ? pinModel : (laneModels[0]?.id ?? "");

  async function createPin() {
    if (disabled || !lane || !model || !reason.trim()) return;
    setPinning(true);
    await runMutation(
      () =>
        fetch("/api/admin/model-pins", {
          method: "POST",
          headers: JSON_HEADERS,
          body: JSON.stringify({ workspaceId, tier: pinTier, provider: lane, model, reason: reason.trim() }),
        }),
      "Unable to pin the tier",
      {
        onError,
        onSuccess: async () => {
          setReason("");
          await onPinned();
        },
      },
    );
    setPinning(false);
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <NativeSelect
        aria-label="Pin tier"
        selectSize="sm"
        value={pinTier}
        onChange={(e) => setPinTier(e.target.value as Tier)}
        wrapperClassName="w-[130px]"
      >
        {tiers.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </NativeSelect>
      <NativeSelect
        aria-label="Pin lane"
        selectSize="sm"
        value={lane ?? ""}
        onChange={(e) => setPinLane(e.target.value as Lane)}
        wrapperClassName="w-[150px]"
      >
        {permittedLanes.map((l) => (
          <option key={l} value={l}>
            {LANE_NAMES[l]}
          </option>
        ))}
      </NativeSelect>
      <NativeSelect
        aria-label="Pin model"
        selectSize="sm"
        value={model}
        onChange={(e) => setPinModel(e.target.value)}
        wrapperClassName="min-w-[240px] flex-1"
      >
        {laneModels.length === 0 && (
          <option value="" disabled>
            {EMPTY_CATALOG_LABEL}
          </option>
        )}
        {laneModels.map((m) => (
          <option key={m.id} value={m.id}>
            {priceLabel(m)}
          </option>
        ))}
      </NativeSelect>
      <Input
        aria-label="Pin reason"
        inputSize="sm"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (required)"
        className="min-w-[180px] flex-1"
      />
      <Button
        type="button"
        size="sm"
        onClick={() => void createPin()}
        disabled={disabled || !lane || !model || !reason.trim() || pinning}
        loading={pinning}
      >
        Pin
      </Button>
      {trailing}
    </div>
  );
}
